import React from 'react';
/* A 112px re-export of the brand mark: the full-size logo.jpg is 91KB
   for a 34px slot, which is the page's only real asset waste. */
import logoMark from '../../assets/images/logo-mark.jpg';
import { STORY, STORY_SKILLS } from '../data/content';
import useMotionPrefs from '../hooks/useMotionPrefs';
import useScrollVar from '../hooks/useScrollVar';
import useInView from '../hooks/useInView';
import SkillTag from '../components/SkillTag';
import CompanyCard from '../components/CompanyCard';

/* ==================================================================
   TalentStory
   ------------------------------------------------------------------
   The centrepiece: a pinned, three-chapter story.

   Desktop - the section is 340vh tall with a sticky viewport inside.
   Its scroll progress lands in --hs-sp and CSS maps that single value
   onto per-chapter windows with clamp(), including the per-element
   stagger (each element offsets the window by its own --hs-i). That
   means the entire choreography runs with zero JS per frame beyond
   one custom-property write.

   Narrow screens / reduced motion - the pin is dropped entirely
   (`is-static`) and the same three chapters become three stacked
   blocks that reveal normally on entry. Same markup, calmer motion.
   ================================================================== */

function Chapter({ index, id, children }) {
  const [ref, inView] = useInView({ threshold: 0.25 });
  return (
    <div
      ref={ref}
      className={`hs-chapter hs-chapter--${index}`}
      data-chapter={id}
      data-in={inView ? 'true' : 'false'}
    >
      <div className="hs-chapter__fade">{children}</div>
    </div>
  );
}

export default function TalentStory() {
  const { reduced, wide } = useMotionPrefs();
  const pinned = wide && !reduced;
  const ref = useScrollVar({ mode: 'pin', varName: '--hs-sp', disabled: !pinned, resetValue: 1 });

  return (
    <section
      id="about"
      className={`hs-story ${pinned ? '' : 'is-static'}`}
      ref={ref}
      aria-label="How HyrSkill connects students and companies"
    >
      <div className="hs-story__viewport">
        <ol className="hs-story__rail" aria-hidden="true">
          <span className="hs-story__rail-line"><span className="hs-story__rail-fill" /></span>
          {STORY.chapters.map((chapter, i) => (
            <li key={chapter.id} className={`hs-story__rail-item hs-story__rail-item--${i + 1}`}>
              <span className="hs-story__rail-dim">{chapter.index}</span>
              <span className="hs-story__rail-lit">
                <span className="hs-story__rail-lit-in">{chapter.index}</span>
              </span>
            </li>
          ))}
        </ol>

        <div className="hs-story__stage">
          {/* 01 - skills */}
          <Chapter index={1} id={STORY.chapters[0].id}>
            <p className="hs-chapter__index">{STORY.chapters[0].index}</p>
            <h2 className="hs-chapter__title">{STORY.chapters[0].title}</h2>
            <p className="hs-chapter__body">{STORY.chapters[0].body}</p>
            <div className="hs-chapter__skills">
              {STORY_SKILLS.map((skill, i) => (
                <span className="hs-staged" style={{ '--hs-i': i }} key={skill}>
                  <SkillTag tone="raised" index={i}>{skill}</SkillTag>
                </span>
              ))}
            </div>
          </Chapter>

          {/* 02 - opportunities */}
          <Chapter index={2} id={STORY.chapters[1].id}>
            <p className="hs-chapter__index">{STORY.chapters[1].index}</p>
            <h2 className="hs-chapter__title">{STORY.chapters[1].title}</h2>
            <p className="hs-chapter__body">{STORY.chapters[1].body}</p>
            <div className="hs-chapter__roles">
              {STORY.roles.map((role, i) => (
                <span className="hs-staged" style={{ '--hs-i': i }} key={role.role}>
                  <CompanyCard role={role.role} company={role.company} tone={i % 2 ? 'plain' : 'blue'} />
                </span>
              ))}
            </div>
          </Chapter>

          {/* 03 - the connection */}
          <Chapter index={3} id={STORY.chapters[2].id}>
            <p className="hs-chapter__index">{STORY.chapters[2].index}</p>
            <h2 className="hs-chapter__title">{STORY.chapters[2].title}</h2>
            <p className="hs-chapter__body">{STORY.chapters[2].body}</p>

            <div className="hs-wire">
              <div className="hs-wire__col hs-wire__col--left">
                {STORY.people.map((person, i) => (
                  <span className="hs-staged" style={{ '--hs-i': i }} key={person.name}>
                    <article className="hs-mini">
                      <span className="hs-mini__avatar">{person.name.slice(0, 1)}</span>
                      <div>
                        <p className="hs-mini__name">{person.name}</p>
                        <p className="hs-mini__meta">{person.track}</p>
                      </div>
                    </article>
                  </span>
                ))}
              </div>

              <div className="hs-wire__hub">
                <svg className="hs-wire__svg" viewBox="0 0 200 200" preserveAspectRatio="none" aria-hidden="true">
                  {[26, 100, 174].map((y, i) => (
                    <path
                      key={`in-${y}`}
                      d={`M0 ${y} C 55 ${y}, 62 100, 100 100`}
                      pathLength="100" vectorEffect="non-scaling-stroke"
                      style={{ '--hs-i': i }}
                    />
                  ))}
                  {[26, 100, 174].map((y, i) => (
                    <path
                      key={`out-${y}`}
                      d={`M100 100 C 138 100, 145 ${y}, 200 ${y}`}
                      pathLength="100" vectorEffect="non-scaling-stroke"
                      style={{ '--hs-i': i + 3 }}
                    />
                  ))}
                </svg>
                <div className="hs-wire__badge">
                  <img src={logoMark} alt="" width="30" height="30" decoding="async" />
                  <span>HyrSkill</span>
                </div>
              </div>

              <div className="hs-wire__col hs-wire__col--right">
                {STORY.openings.map((opening, i) => (
                  <span className="hs-staged" style={{ '--hs-i': i + 3 }} key={opening.role}>
                    <CompanyCard role={opening.role} company={opening.company} tone="blue" />
                  </span>
                ))}
              </div>
            </div>
          </Chapter>
        </div>
      </div>
    </section>
  );
}
